import { useMutation, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  useBlocker,
  useNavigate,
} from "@tanstack/react-router";
import { AxiosError } from "axios";
import { useEffect, useMemo, useRef, useState } from "react";
import { Editor } from "tldraw";

import {
  applyResolutions,
  embedRequests,
} from "@/components/canvas-editor/resolve-embeds";
import {
  CustomNodeEditorContext,
  EditingCustomNodeIdContext,
} from "@/components/canvas-editor/utils";
import { CustomNodeColorSelect } from "@/components/CustomNodeColorSelect";
import { EmbeddedCopiesBanner } from "@/components/EmbeddedCopiesBanner";
import { FeedEditor, FeedEditorHandle } from "@/components/FeedEditor";
import { FeedForm } from "@/components/FeedForm";
import { PublishReachNotice } from "@/components/PublishReachNotice";
import { ResolvedEmbedsNotice } from "@/components/ResolvedEmbedsNotice";
import { RichTextEditor } from "@/components/RichTextEditor";
import { Button, ButtonLink } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { H1 } from "@/components/ui/typography";
import {
  editCustomNode,
  getCustomNodeQueryOptions,
  resolveEmbedsQueryOptions,
  getUserCustomNodesQueryOptions,
  searchCustomNodesQueryOptions,
} from "@/lib/api";
import { getSnapshot } from "@/lib/getSnapshot";
import { FeedVersion } from "@/lib/types";

export const Route = createFileRoute(
  "/app/_sidebar-layout/custom-nodes/$nodeId/edit",
)({
  component: RouteComponent,
  validateSearch: (search: {
    fullscreen?: boolean;
  }): { fullscreen?: boolean; customNode?: number } => {
    // validate and parse the search params into a typed state
    return {
      fullscreen: search?.fullscreen === true || undefined,
    };
  },
  loader: async ({ context: { queryClient }, params: { nodeId } }) => {
    void queryClient.ensureQueryData(getUserCustomNodesQueryOptions());
    void queryClient.prefetchInfiniteQuery(
      searchCustomNodesQueryOptions({ query: "", favorited_only: true }),
    );

    const data = await queryClient.ensureQueryData(
      getCustomNodeQueryOptions(parseInt(nodeId)),
    );

    return {
      title: data.title,
    };
  },
  head: ({ loaderData }) => ({
    meta: [{ title: loaderData?.title }],
  }),
});

function RouteComponent() {
  const feedEditorRef = useRef<FeedEditorHandle>(null!);
  const { fullscreen } = Route.useSearch();
  const editorRef = useRef<Editor>(null!);
  const { nodeId } = Route.useParams();
  const navigate = useNavigate({ from: "/app/custom-nodes/$nodeId/edit" });
  const { data } = useSuspenseQuery(
    getCustomNodeQueryOptions(parseInt(nodeId)),
  );
  const [description, setDescription] = useState(data.description);
  const [changeLog, setChangeLog] = useState("");
  const shouldBlock = useRef(true);
  // What the FEED will run, not what was frozen into the body when each node
  // was embedded. Under a `compatible` intent the server resolves an embed
  // forward at compile time, so rendering the stored bytes would show the
  // author logic their feed is not running -- and the gap only appears once
  // the source publishes, which is exactly when nobody is looking.
  //
  // Resolve first, rewrite the manifest, then build the canvas from the
  // rewritten one: correct by construction rather than correct-until-someone-
  // publishes. The server answers only WHICH version; the expansion stays here,
  // because the browser's expander is the byte-parity reference.
  const requests = useMemo(() => embedRequests(data.component), [data.component]);
  const { data: resolution } = useQuery({
    ...resolveEmbedsQueryOptions(requests),
    enabled: requests.length > 0,
  });

  const resolved = useMemo(
    () => applyResolutions(data.component, resolution?.resolved ?? []),
    [data.component, resolution],
  );

  const [feedVersion, setFeedVersion] = useState<FeedVersion>({
    manifest: {
      filter: data.component,
    },
    metadata: { snapshot: data.component_metadata?.snapshot },
    order: "new",
  } as FeedVersion);

  // The resolution arrives after the first render, so the canvas is built from
  // the stored bytes and corrected once, and only when something actually
  // moved -- rebuilding on every load would discard unsaved edits for nothing.
  //
  // Adjusted DURING RENDER rather than in an effect. React re-runs this
  // component before touching the DOM, so there is no flash of the stale
  // canvas and no cascading render; an effect here would paint the old bytes
  // first and then replace them.
  const [resolutionApplied, setResolutionApplied] = useState(false);
  if (!resolutionApplied && resolved.moved.length > 0) {
    setResolutionApplied(true);
    setFeedVersion((current) => ({
      ...current,
      manifest: { ...current.manifest, filter: resolved.body },
    }));
  }
  const onSubmit = useMutation({
    mutationFn: async (formData: FormData) => {
      const filter = feedVersion?.manifest.filter;

      if (!filter) {
        throw new AxiosError("No filter found");
      }

      const node = await editCustomNode({
        id: data.id,
        description,
        title: formData.get("display_name") as string,
        public: formData.get("public") === "on",
        component: feedVersion?.manifest.filter,
        color: formData.get("color") as string,
        version_message: changeLog,
        component_metadata: {
          snapshot: getSnapshot(feedVersion),
        },
      });

      return node;
    },
    onSuccess: async () => {
      shouldBlock.current = false;
      await navigate({
        to: "/app/custom-nodes/$nodeId/view",
        params: { nodeId: `${data.id}` },
      });
    },
    onError: (error: AxiosError) => console.error(error),
  });

  useBlocker({
    shouldBlockFn: (event) => {
      if (event.action === "REPLACE") {
        return false;
      }

      if (shouldBlock.current) {
        const shouldNav = confirm(
          "Are you sure you want to leave? This will discard all your changes.",
        );
        return !shouldNav;
      }

      return false;
    },
  });

  const headerInput = useRef<HTMLInputElement>(null!);

  useEffect(() => {
    if (headerInput.current && !fullscreen) {
      headerInput.current.focus();
    }
  }, [fullscreen]);

  return (
    <FeedForm
      onSubmit={onSubmit.mutate}
      feedEditorRef={feedEditorRef}
      feedVersion={feedVersion}
      editorRef={editorRef}
    >
      <div className="flex flex-col gap-2 border-b border-slate-6 dark:border-slatedark-6 pb-4">
        <div className="text-slate-dim">Edit Custom Node</div>
        <div className="flex items-center gap-2 justify-between">
          <H1 className="border-b-0 flex-1">
            <input
              required
              ref={headerInput}
              defaultValue={data.title}
              name="display_name"
              placeholder="Node Name"
              className="w-full bg-transparent placeholder:text-slate-dim focus-visible:outline-hidden focus-visible:ring-2  focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 rounded-lg"
              autoComplete="off"
            />
          </H1>
          <div className="flex items-center gap-2">
            <ButtonLink
              to="/app/custom-nodes/$nodeId/view"
              params={{ nodeId: `${data.id}` }}
              variant="outline"
            >
              Cancel
            </ButtonLink>
            <Button type="submit" isLoading={onSubmit.isPending}>
              Save
            </Button>
          </div>
        </div>
      </div>

      <div className="flex items-center">
        <div className="flex-1">
          <div className="flex flex-col gap-1 max-w-[60ch]">
            <Label htmlFor="public-mode">Public</Label>
            <div className="text-sm text-slate-dim">
              Set whether the node is public so other users can use it in their
              feeds.
            </div>
          </div>
        </div>
        <Switch name="public" id="public-mode" defaultChecked={data.public} />
      </div>

      <CustomNodeColorSelect
        editorRef={editorRef}
        defaultValue={data.component.metadata?.color}
      />

      <div className="flex flex-col gap-3">
        <Label htmlFor="description">Node Description</Label>

        <RichTextEditor
          placeholder="Describe your node"
          id="description"
          value={description}
          onChange={setDescription}
          variant="full"
        />
      </div>

      <EmbeddedCopiesBanner componentId={parseInt(nodeId)} />
      <ResolvedEmbedsNotice moved={resolved.moved} />

      <CustomNodeEditorContext value={true}>
        <EditingCustomNodeIdContext value={parseInt(nodeId)}>
          <FeedEditor
            handle={feedEditorRef}
            editorRef={editorRef}
            feedVersion={feedVersion}
            onChange={setFeedVersion}
            defaultFullscreen={fullscreen}
            fullscreenActions={
              <Button size="sm" isLoading={onSubmit.isPending} type="submit">
                Save
              </Button>
            }
            centerContent={true}
            rootNode={{
              title: "Custom Node",
              variant: "custom-node",
              description:
                "This is a definition of a custom node that you can share with other feeds.",
              customNodeParameters: data.component.metadata?.customNodeParameters,
            }}
          />
        </EditingCustomNodeIdContext>
      </CustomNodeEditorContext>

      <div className="flex flex-col gap-3">
        <Label htmlFor="change-log">Change Log (optional)</Label>

        <RichTextEditor
          placeholder="What changed in this version?"
          id="change-log"
          value={changeLog}
          onChange={setChangeLog}
          variant="full"
        />

        {/* Next to the change log rather than beside the button: this is context
            for what you are about to write down, and a number that only appears
            at the moment of clicking is a number people learn to click past. */}
        <PublishReachNotice componentId={parseInt(nodeId)} />
      </div>
    </FeedForm>
  );
}
